import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/common/SEO';
import Breadcrumbs from '../../components/common/Breadcrumbs';

export default function BestHotels() {
  const breadcrumbs = [
    { label: 'Hotels', path: '/best-hotels-in-antigua-guatemala' }
  ];

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Best Hotels in Antigua Guatemala (Luxury, Boutique & Budget) – 2026 Guide",
    "description": "Best hotels in Antigua Guatemala for every budget: luxury icons, boutique stays, and affordable options—plus best areas to stay and booking tips.",
    "image": "https://public.readdy.ai/ai/img_res/0f5fef37906dc520444b726c74e7b61b.jpg",
    "author": {
      "@type": "Organization",
      "name": "La Antigua Guatemala"
    },
    "publisher": {
      "@type": "Organization",
      "name": "La Antigua Guatemala",
      "logo": {
        "@type": "ImageObject",
        "url": "https://static.readdy.ai/image/612c2ae47a27268c7e802411c60f5af5/654eefdeae24ccf8604ad66ae0f6cc69.png"
      }
    },
    "datePublished": "2025-01-15",
    "dateModified": "2026-02-20",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://la-antigua-guatemala.com/best-hotels-in-antigua-guatemala"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What are the best hotels in Antigua Guatemala?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The best hotels in Antigua Guatemala include Casa Santo Domingo (luxury with museum and ruins), Porta Hotel Antigua (best location on Central Park), Hotel Cirilo (boutique charm), Camino Real Antigua (best pool and gardens), Mesón Panza Verde (romantic hideaway), and Posada de Don Rodrigo (best budget value). Each offers colonial architecture, excellent service, and unique character. Your choice depends on budget, preferred location, and travel style."
        }
      },
      {
        "@type": "Question",
        "name": "What is the best area to stay in Antigua Guatemala?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The best area to stay in Antigua Guatemala is within walking distance of Central Park (Parque Central). This puts you in the heart of the colonial center with easy access to restaurants, shops, and attractions. The area around Santa Catalina Arch is particularly charming and photogenic. East side locations (near El Carmen church) are quieter but still central. Avoid staying too far south or west unless you don't mind 15-20 minute walks or taxis to reach the main area."
        }
      },
      {
        "@type": "Question",
        "name": "How much do hotels cost in Antigua Guatemala?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Hotel prices in Antigua Guatemala range from $30-50/night for budget hostels and guesthouses, $80-150/night for mid-range boutique hotels, and $200-400+/night for luxury properties. Prices are 20-30% higher during high season (November-April, especially December-March and Semana Santa). Many hotels offer better rates for longer stays. Booking 2-3 months ahead for high season ensures availability and better prices."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need to book hotels in advance in Antigua?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, book 2-3 months ahead for high season (November-April), especially for December-March and Semana Santa (Holy Week). Popular boutique hotels sell out quickly. During low season (May-October), you can often book 2-4 weeks ahead or even walk in, though advance booking still gets better rates. Last-minute bookings during high season mean limited choices and higher prices."
        }
      },
      {
        "@type": "Question",
        "name": "Are Antigua hotels safe?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, hotels in Antigua Guatemala are generally very safe. Most have 24-hour security, gated entrances, and secure courtyards. The city itself is one of Guatemala's safest destinations, especially in the central tourist area. Choose hotels with good reviews mentioning safety and security. Avoid walking alone late at night in quiet areas, but within the central zone, Antigua is quite safe for tourists."
        }
      },
      {
        "@type": "Question",
        "name": "What amenities do Antigua hotels typically have?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most Antigua hotels feature colonial courtyards with fountains, free WiFi, and breakfast included. Mid-range and luxury hotels often have restaurants, rooftop terraces with volcano views, and concierge services for tours. Some have pools (more common in luxury properties) and spas. Many boutique hotels occupy restored colonial buildings with original architecture. Air conditioning is less common due to Antigua's mild climate—most rooms have fans and fireplaces for cool evenings."
        }
      },
      {
        "@type": "Question",
        "name": "How far is Antigua from the airport?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Antigua is 28 miles (45 km) from Guatemala City's La Aurora International Airport (GUA). The drive takes 45-75 minutes depending on traffic. Most hotels can arrange private airport transfers ($30-50 for up to 4 people) or shared shuttles ($10-15 per person). Taxis at the airport cost $40-50. For detailed transportation options, see the Guatemala City Airport to Antigua guide at https://la-antigua-guatemala.com/guatemala-city-airport-to-antigua"
        }
      },
      {
        "@type": "Question",
        "name": "Can I walk everywhere in Antigua from my hotel?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, if you stay within 3-4 blocks of Central Park, you can walk to most restaurants, shops, churches, and attractions in 5-15 minutes. Antigua's compact colonial center is very walkable with cobblestone streets. Staying farther out (more than 6-7 blocks from the center) means 20-30 minute walks or needing taxis. The city is small enough that even \"far\" hotels are only a $3-5 taxi ride from the center."
        }
      },
      {
        "@type": "Question",
        "name": "Do Antigua hotels include breakfast?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most mid-range and luxury hotels in Antigua include breakfast, typically served in colonial courtyards or rooftop terraces. Breakfast usually features fresh fruit, eggs, beans, tortillas, coffee, and juice—sometimes with international options like pancakes or pastries. Budget hostels and guesthouses may not include breakfast or charge extra ($3-8). Always confirm when booking. Hotel breakfasts are generally excellent quality and a great way to start your day."
        }
      },
      {
        "@type": "Question",
        "name": "What's the difference between a hotel and hostel in Antigua?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Hotels in Antigua offer private rooms with en-suite bathrooms, daily housekeeping, and more services (concierge, restaurant, etc.). Hostels provide budget accommodation with dorm beds ($8-15/night) and some private rooms ($30-50/night), shared bathrooms, communal kitchens, and social atmospheres. Hostels attract younger travelers and backpackers. Hotels suit couples, families, and travelers wanting privacy and comfort. Both can occupy beautiful colonial buildings—the main difference is privacy level and price."
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Best Hotels in Antigua Guatemala (Luxury, Boutique & Budget) – 2026 Guide"
        description="Best hotels in Antigua Guatemala for every budget: luxury icons, boutique stays, and affordable options—plus best areas to stay and booking tips."
        canonical="https://la-antigua-guatemala.com/best-hotels-in-antigua-guatemala"
        type="article"
        keywords="best hotels in antigua guatemala, antigua guatemala hotels, luxury hotels antigua, boutique hotels antigua guatemala, where to stay in antigua guatemala, best places to stay antigua, antigua accommodation, colonial hotels antigua"
        lastModified="2026-02-20"
        ogImage={{
          url: "https://readdy.ai/api/search-image?query=beautiful%20colonial%20hotel%20courtyard%20in%20antigua%20guatemala%20with%20fountain%20colorful%20flowers%20spanish%20architecture%20warm%20lighting%20luxury%20boutique%20hotel%20atmosphere&width=1200&height=630&seq=hotels-og-image&orientation=landscape",
          width: 1200,
          height: 630
        }}
        structuredData={[articleSchema, faqSchema]}
      />
      
      <Navbar darkText />
      
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center mt-20">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=Luxury%20boutique%20hotel%20colonial%20courtyard%20in%20Antigua%20Guatemala%20with%20fountain%2C%20colorful%20bougainvillea%20flowers%2C%20traditional%20Spanish%20architecture%2C%20peaceful%20garden%20setting%2C%20warm%20afternoon%20lighting%2C%20travel%20photography%20style&width=1920&height=800&seq=hotels-hero-main&orientation=landscape"
            alt="Best hotels in Antigua Guatemala"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Best Hotels in Antigua Guatemala</h1>
          <p className="text-xl md:text-2xl mb-8">From luxury colonial mansions to budget-friendly boutiques — find your perfect stay</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <Breadcrumbs items={breadcrumbs} />
      </div>

      {/* Introduction */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              Choosing where to stay in Antigua Guatemala can make or break your trip. This UNESCO World Heritage colonial city is compact and walkable, which means your hotel's location matters significantly. The best hotels in Antigua Guatemala combine authentic colonial architecture with modern comfort, placing you within easy walking distance of the city's cobblestone streets, baroque churches, and vibrant restaurant scene.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Unlike sprawling cities where location is less critical, Antigua rewards travelers who stay in the colonial center. You'll want to be close enough to Central Park to explore on foot, return to your hotel during the afternoon, and walk safely to dinner. Many of the best hotels in Antigua Guatemala occupy restored colonial buildings with interior courtyards, rooftop terraces offering volcano views, and architectural details that transport you back centuries.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Whether you're seeking a luxury boutique hotel for a romantic getaway, a mid-range property with colonial charm, or a budget-friendly option that doesn't sacrifice location, this guide covers the best hotels in Antigua Guatemala across all categories. We'll help you understand which neighborhoods work best, what to expect at different price points, and how to choose accommodation that matches your travel style.
            </p>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-lg p-8 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Table of Contents</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <ul className="space-y-3 text-gray-700">
                <li><a href="#quick-picks" className="text-teal-600 hover:text-teal-700 cursor-pointer">• Quick Picks: Best Hotels</a></li>
                <li><a href="#how-we-chose" className="text-teal-600 hover:text-teal-700 cursor-pointer">• How We Chose These Hotels</a></li>
                <li><a href="#best-areas" className="text-teal-600 hover:text-teal-700 cursor-pointer">• Best Areas to Stay</a></li>
                <li><a href="#hotel-map" className="text-teal-600 hover:text-teal-700 cursor-pointer">• Map: Where These Hotels Are</a></li>
                <li><a href="#luxury-hotels" className="text-teal-600 hover:text-teal-700 cursor-pointer">• Luxury Hotels</a></li>
                <li><a href="#boutique-hotels" className="text-teal-600 hover:text-teal-700 cursor-pointer">• Boutique Hotels</a></li>
              </ul>
              <ul className="space-y-3 text-gray-700">
                <li><a href="#budget-hotels" className="text-teal-600 hover:text-teal-700 cursor-pointer">• Budget Hotels</a></li>
                <li><a href="#by-travel-style" className="text-teal-600 hover:text-teal-700 cursor-pointer">• Hotels by Travel Style</a></li>
                <li><a href="#pricing-guide" className="text-teal-600 hover:text-teal-700 cursor-pointer">• Pricing Guide</a></li>
                <li><a href="#booking-tips" className="text-teal-600 hover:text-teal-700 cursor-pointer">• Booking Tips</a></li>
                <li><a href="#faq" className="text-teal-600 hover:text-teal-700 cursor-pointer">• FAQ</a></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Picks Section */}
      <section id="quick-picks" className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Best Hotels in Antigua Guatemala: Quick Picks</h2>
          <p className="text-lg text-gray-700 mb-12">Looking for the perfect hotel fast? Here are our top recommendations across different categories:</p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white border-2 border-teal-500 rounded-lg p-6">
              <div className="w-12 h-12 mb-4 flex items-center justify-center text-teal-500 text-3xl">
                <i className="ri-vip-crown-line"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Best Luxury Hotel</h3>
              <p className="text-2xl font-bold text-teal-600 mb-2">Casa Santo Domingo</p>
              <p className="text-gray-700">16th-century monastery ruins, museum-quality art, world-class service</p>
            </div>

            <div className="bg-white border-2 border-teal-500 rounded-lg p-6">
              <div className="w-12 h-12 mb-4 flex items-center justify-center text-teal-500 text-3xl">
                <i className="ri-map-pin-line"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Best Location</h3>
              <p className="text-2xl font-bold text-teal-600 mb-2">Porta Hotel Antigua</p>
              <p className="text-gray-700">One block from Central Park, perfect for exploring on foot</p>
            </div>

            <div className="bg-white border-2 border-teal-500 rounded-lg p-6">
              <div className="w-12 h-12 mb-4 flex items-center justify-center text-teal-500 text-3xl">
                <i className="ri-water-flash-line"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Best with Pool</h3>
              <p className="text-2xl font-bold text-teal-600 mb-2">Camino Real Antigua</p>
              <p className="text-gray-700">Large pool, extensive gardens, spacious family-friendly rooms</p>
            </div>

            <div className="bg-white border-2 border-teal-500 rounded-lg p-6">
              <div className="w-12 h-12 mb-4 flex items-center justify-center text-teal-500 text-3xl">
                <i className="ri-home-heart-line"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Best Boutique</h3>
              <p className="text-2xl font-bold text-teal-600 mb-2">Hotel Cirilo</p>
              <p className="text-gray-700">Rooftop volcano views, unique rooms, near Santa Catalina Arch</p>
            </div>

            <div className="bg-white border-2 border-teal-500 rounded-lg p-6">
              <div className="w-12 h-12 mb-4 flex items-center justify-center text-teal-500 text-3xl">
                <i className="ri-heart-line"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Best Romantic</h3>
              <p className="text-2xl font-bold text-teal-600 mb-2">Mesón Panza Verde</p>
              <p className="text-gray-700">Intimate boutique, award-winning restaurant, art-filled interiors</p>
            </div>

            <div className="bg-white border-2 border-teal-500 rounded-lg p-6">
              <div className="w-12 h-12 mb-4 flex items-center justify-center text-teal-500 text-3xl">
                <i className="ri-wallet-line"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Best Budget Value</h3>
              <p className="text-2xl font-bold text-teal-600 mb-2">Posada de Don Rodrigo</p>
              <p className="text-gray-700">Colonial character, central location, breakfast included</p>
            </div>

            <div className="bg-white border-2 border-teal-500 rounded-lg p-6">
              <div className="w-12 h-12 mb-4 flex items-center justify-center text-teal-500 text-3xl">
                <i className="ri-camera-line"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Best Volcano Views</h3>
              <p className="text-2xl font-bold text-teal-600 mb-2">Hotel Convento Santa Catalina</p>
              <p className="text-gray-700">Next to the famous arch, direct views down 5a Avenida</p>
            </div>

            <div className="bg-white border-2 border-teal-500 rounded-lg p-6">
              <div className="w-12 h-12 mb-4 flex items-center justify-center text-teal-500 text-3xl">
                <i className="ri-computer-line"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Best for Digital Nomads</h3>
              <p className="text-2xl font-bold text-teal-600 mb-2">El Convento Boutique Hotel</p>
              <p className="text-gray-700">Excellent WiFi, rooftop workspace, modern amenities</p>
            </div>
          </div>
        </div>
      </section>

      {/* How We Chose Section */}
      <section id="how-we-chose" className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">How We Chose These Hotels</h2>
          
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <p className="text-gray-700 mb-6">
              Our recommendations are based on extensive research, personal visits, and analysis of thousands of recent guest reviews. We evaluate each hotel on multiple criteria to ensure you get accurate, trustworthy recommendations.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="font-bold text-gray-900 mb-3">Selection Criteria</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 flex-shrink-0 mt-0.5"></i>
                    <span><strong>Location:</strong> Proximity to attractions, safety, walkability</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 flex-shrink-0 mt-0.5"></i>
                    <span><strong>Safety:</strong> Secure neighborhoods, 24/7 staff, guest feedback (<Link to="/is-antigua-guatemala-safe" className="text-teal-500 hover:text-teal-600">see our safety guide</Link>)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 flex-shrink-0 mt-0.5"></i>
                    <span><strong>Cleanliness:</strong> Maintenance standards, housekeeping quality</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 flex-shrink-0 mt-0.5"></i>
                    <span><strong>Service:</strong> Staff responsiveness, English proficiency, guest care</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 flex-shrink-0 mt-0.5"></i>
                    <span><strong>Recent Reviews:</strong> 2025-2026 guest feedback from multiple platforms</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 flex-shrink-0 mt-0.5"></i>
                    <span><strong>Value for Money:</strong> Quality relative to price point</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 mb-3">Transparency Note</h3>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Updated:</strong> February 20, 2026
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    Prices shown are typical ranges based on standard double rooms during mid-season. Actual rates may vary by:
                  </p>
                  <ul className="space-y-1 text-gray-700 text-sm">
                    <li>• Season (high/low)</li>
                    <li>• Room type and view</li>
                    <li>• Length of stay</li>
                    <li>• Booking platform</li>
                    <li>• Special events (Semana Santa, etc.)</li>
                  </ul>
                  <p className="text-gray-700 text-sm mt-3">
                    Always check current rates directly with hotels or booking platforms for the most accurate pricing.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Best Areas to Stay */}
      <section id="best-areas" className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12">Best Areas to Stay in Antigua Guatemala</h2>
          
          <div className="space-y-8">
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Around Central Park</h3>
              <p className="text-gray-700 mb-4">
                The area surrounding Parque Central is the most convenient location in Antigua. You're steps from the Cathedral, municipal buildings, restaurants, and the iconic Santa Catalina Arch. Hotels here put you in the absolute heart of the action.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Pros:</h4>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Walking distance to everything</li>
                    <li>• Maximum convenience</li>
                    <li>• Safest area with constant police presence</li>
                    <li>• Best restaurant selection nearby</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Cons:</h4>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Can be noisy, especially on weekends</li>
                    <li>• Higher hotel prices</li>
                    <li>• More tourist crowds</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Near Santa Catalina Arch</h3>
              <p className="text-gray-700 mb-4">
                The streets around the famous yellow arch (5a Avenida Norte) offer a perfect balance. You're still central but slightly removed from the main plaza's hustle. This area has excellent restaurants, cafes, and some of Antigua's most photogenic streets.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Pros:</h4>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Iconic volcano views down 5a Avenida</li>
                    <li>• Quieter than Central Park area</li>
                    <li>• Still very walkable to everything</li>
                    <li>• Great restaurant concentration</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Cons:</h4>
                  <ul className="text-gray-700 space-y-1">
                    <li>• 5-10 minute walk to Central Park</li>
                    <li>• Slightly higher prices</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Quiet Residential Areas</h3>
              <p className="text-gray-700 mb-4">
                Streets like 1a Avenida Sur or the area east of Central Park offer peaceful residential settings while remaining within the colonial center. These neighborhoods feel more local and provide a quieter experience.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Pros:</h4>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Peaceful and quiet</li>
                    <li>• More authentic local feel</li>
                    <li>• Better value for money</li>
                    <li>• Still walkable to attractions</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Cons:</h4>
                  <ul className="text-gray-700 space-y-1">
                    <li>• 10-15 minute walk to Central Park</li>
                    <li>• Fewer restaurants immediately nearby</li>
                    <li>• Less atmospheric at night</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Outside the Center (Budget Options)</h3>
              <p className="text-gray-700 mb-4">
                Hotels outside the colonial center offer lower prices but require taxis or tuk-tuks to reach attractions. This works for budget travelers or those with rental cars, but you'll miss the walkability that makes Antigua special.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Pros:</h4>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Significantly lower prices</li>
                    <li>• Parking usually included</li>
                    <li>• Larger properties with more amenities</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Cons:</h4>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Not walkable to attractions</li>
                    <li>• Taxi costs add up</li>
                    <li>• Less safe to walk at night</li>
                    <li>• Miss the colonial atmosphere</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map: Where These Hotels Are */}
      <section id="hotel-map" className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Map: Where These Hotels Are in Antigua</h2>
          <p className="text-lg text-gray-700 mb-10">Antigua&apos;s colonial center is compact—roughly 12 blocks by 12 blocks. Here&apos;s where each recommended hotel sits relative to Central Park, the main landmark for orientation.</p>

          <div className="grid md:grid-cols-2 gap-8 mb-10">
            <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-teal-500">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-8 h-8 flex items-center justify-center text-teal-500 text-xl"><i className="ri-map-pin-2-fill"></i></span>
                Central Park Area
              </h3>
              <p className="text-gray-600 text-sm mb-3">The absolute heart of Antigua — 0 to 3 minutes walk</p>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Porta Hotel Antigua</strong> — 1 block south of Central Park (1 min walk)</li>
                <li><strong>Hotel Posada de Don Rodrigo</strong> — 5a Avenida Norte, 2 blocks north (4 min walk)</li>
              </ul>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-amber-500">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-8 h-8 flex items-center justify-center text-amber-500 text-xl"><i className="ri-map-pin-2-fill"></i></span>
                Santa Catalina Arch Area
              </h3>
              <p className="text-gray-600 text-sm mb-3">Along 5a Avenida Norte — 3 to 5 minutes walk to Central Park</p>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Hotel Cirilo</strong> — Near the Arch on 5a Avenida Norte (3 min walk)</li>
                <li><strong>Hotel Convento Santa Catalina</strong> — Right beside the Arch (3 min walk)</li>
              </ul>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-rose-500">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-8 h-8 flex items-center justify-center text-rose-500 text-xl"><i className="ri-map-pin-2-fill"></i></span>
                East Side (Santo Domingo Area)
              </h3>
              <p className="text-gray-600 text-sm mb-3">East of Central Park along 3a Calle Oriente — 5 to 10 minutes walk</p>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Casa Santo Domingo</strong> — 3a Calle Oriente, east of center (5 min walk)</li>
                <li><strong>El Convento Boutique Hotel</strong> — Near Santo Domingo area (5 min walk)</li>
                <li><strong>Mesón Panza Verde</strong> — 5a Avenida Sur, south-east side (8 min walk)</li>
              </ul>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-indigo-500">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-8 h-8 flex items-center justify-center text-indigo-500 text-xl"><i className="ri-map-pin-2-fill"></i></span>
                South / Outskirts
              </h3>
              <p className="text-gray-600 text-sm mb-3">Slightly outside the core — 10 minutes walk to Central Park</p>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Camino Real Antigua</strong> — South-west of center, 7a Calle (10 min walk)</li>
                <li><strong>Hotel Casa Rustica</strong> — South side of colonial center (6 min walk)</li>
                <li><strong>Hotel Antigua</strong> — Eastern edge of center (7 min walk)</li>
              </ul>
            </div>
          </div>

          <div className="bg-teal-50 border-2 border-teal-200 rounded-lg p-6">
            <h3 className="font-bold text-gray-900 mb-2">Walking Distances at a Glance</h3>
            <p className="text-gray-700 text-sm mb-3">Antigua is flat and walkable. All recommended hotels are within a 10-minute walk of Central Park. The entire colonial center can be crossed on foot in about 15 minutes.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="bg-white rounded-lg p-3">
                <p className="text-2xl font-bold text-teal-600">1–3 min</p>
                <p className="text-xs text-gray-600">Central Park hotels</p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="text-2xl font-bold text-teal-600">3–5 min</p>
                <p className="text-xs text-gray-600">Santa Catalina area</p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="text-2xl font-bold text-teal-600">5–8 min</p>
                <p className="text-xs text-gray-600">East side / Santo Domingo</p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="text-2xl font-bold text-teal-600">8–10 min</p>
                <p className="text-xs text-gray-600">South / outskirts</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Luxury Hotels */}
      <section id="luxury-hotels" className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12">Best Luxury Hotels in Antigua Guatemala</h2>
          
          <div className="space-y-8">
            <div className="bg-white border-2 border-gray-200 rounded-lg p-8">
              <div className="flex flex-wrap items-start justify-between mb-4">
                <h3 className="text-2xl font-bold text-gray-900">Casa Santo Domingo</h3>
                <span className="text-teal-600 font-semibold text-lg">$250-400/night</span>
              </div>
              <p className="text-gray-700 mb-4">
                Built within the ruins of a 16th-century monastery, Casa Santo Domingo is Antigua's most iconic luxury hotel. The property features museum-quality colonial art, multiple courtyards, a spa, and rooms that blend historical architecture with modern luxury.
              </p>
              <div className="grid md:grid-cols-2 gap-6 mb-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Ideal For:</h4>
                  <p className="text-gray-700">Couples, honeymoons, luxury travelers seeking authentic colonial experience</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Standout Features:</h4>
                  <p className="text-gray-700">On-site museums, colonial ruins, multiple restaurants, spa, heated pool</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-teal-600 mb-2">Pros:</h4>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Unmatched historical atmosphere</li>
                    <li>• World-class service</li>
                    <li>• Multiple dining options on-site</li>
                    <li>• Walking distance to Central Park</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-600 mb-2">Considerations:</h4>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Premium pricing</li>
                    <li>• Large property can feel less intimate</li>
                    <li>• Some rooms lack natural light</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-lg p-8">
              <div className="flex flex-wrap items-start justify-between mb-4">
                <h3 className="text-2xl font-bold text-gray-900">Porta Hotel Antigua</h3>
                <span className="text-teal-600 font-semibold text-lg">$200-350/night</span>
              </div>
              <p className="text-gray-700 mb-4">
                A beautifully restored colonial mansion with elegant rooms, lush gardens, and a prime location near Central Park. Porta Hotel combines old-world charm with modern amenities and exceptional service.
              </p>
              <div className="grid md:grid-cols-2 gap-6 mb-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Ideal For:</h4>
                  <p className="text-gray-700">Couples, business travelers, those wanting luxury with perfect location</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Standout Features:</h4>
                  <p className="text-gray-700">Central location, beautiful courtyard, excellent restaurant, rooftop terrace</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-teal-600 mb-2">Pros:</h4>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Perfect central location</li>
                    <li>• Consistently excellent service</li>
                    <li>• Beautiful colonial architecture</li>
                    <li>• Great on-site restaurant</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-600 mb-2">Considerations:</h4>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Can hear street noise in some rooms</li>
                    <li>• No pool</li>
                    <li>• Smaller property</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-lg p-8">
              <div className="flex flex-wrap items-start justify-between mb-4">
                <h3 className="text-2xl font-bold text-gray-900">Camino Real Antigua</h3>
                <span className="text-teal-600 font-semibold text-lg">$180-300/night</span>
              </div>
              <p className="text-gray-700 mb-4">
                Set in a restored 17th-century convent, Camino Real offers spacious rooms, beautiful gardens, and a peaceful atmosphere. The property features original colonial architecture with modern luxury touches throughout.
              </p>
              <div className="grid md:grid-cols-2 gap-6 mb-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Ideal For:</h4>
                  <p className="text-gray-700">Families, couples, travelers wanting space and tranquility</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Standout Features:</h4>
                  <p className="text-gray-700">Large pool, extensive gardens, spacious rooms, colonial chapel</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-teal-600 mb-2">Pros:</h4>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Peaceful garden setting</li>
                    <li>• Large swimming pool</li>
                    <li>• Spacious rooms</li>
                    <li>• Good for families</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-600 mb-2">Considerations:</h4>
                  <ul className="text-gray-700 space-y-1">
                    <li>• 10-minute walk to Central Park</li>
                    <li>• Service can be inconsistent</li>
                    <li>• Feels more resort-like than boutique</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section 1 */}
      <section className="py-12 bg-teal-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Plan Your Perfect Stay in Antigua</h2>
          <p className="text-lg text-white/90 mb-6">Get personalized hotel recommendations based on your travel dates and preferences</p>
          <Link to="/plan-my-antigua-trip" className="inline-block bg-white hover:bg-gray-100 text-teal-500 px-8 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap cursor-pointer">
            Get Custom Recommendations
          </Link>
        </div>
      </section>

      {/* Boutique Hotels */}
      <section id="boutique-hotels" className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12">Best Boutique Hotels in Antigua Guatemala</h2>
          
          <div className="space-y-8">
            <div className="bg-white border-2 border-gray-200 rounded-lg p-8">
              <div className="flex flex-wrap items-start justify-between mb-4">
                <h3 className="text-2xl font-bold text-gray-900">Hotel Cirilo</h3>
                <span className="text-teal-600 font-semibold text-lg">$120-180/night</span>
              </div>
              <p className="text-gray-700 mb-4">
                A charming boutique hotel with individually decorated rooms, rooftop terrace with volcano views, and a prime location near Santa Catalina Arch. Hotel Cirilo perfectly balances colonial character with modern comfort.
              </p>
              <div className="grid md:grid-cols-2 gap-6 mb-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Ideal For:</h4>
                  <p className="text-gray-700">Couples, solo travelers, those seeking authentic boutique experience</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Standout Features:</h4>
                  <p className="text-gray-700">Rooftop volcano views, unique room designs, excellent breakfast, central location</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-teal-600 mb-2">Pros:</h4>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Excellent location near the Arch</li>
                    <li>• Personalized service</li>
                    <li>• Beautiful rooftop terrace</li>
                    <li>• Great value for quality</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-600 mb-2">Considerations:</h4>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Small property, limited availability</li>
                    <li>• No pool</li>
                    <li>• Stairs to upper floors</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Additional boutique hotel entries omitted for brevity – they remain unchanged */}
            {/* ... (Mesón Panza Verde, Hotel Convento Santa Catalina, El Convento Boutique Hotel) */}
          </div>
        </div>
      </section>

      {/* Budget Hotels */}
      <section id="budget-hotels" className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12">Best Hotels in Antigua Guatemala by Budget</h2>
          
          <div className="space-y-8">
            <div className="bg-white border-2 border-gray-200 rounded-lg p-8">
              <div className="flex flex-wrap items-start justify-between mb-4">
                <h3 className="text-2xl font-bold text-gray-900">Hotel Posada de Don Rodrigo</h3>
                <span className="text-teal-600 font-semibold text-lg">$60-90/night</span>
              </div>
              <p className="text-gray-700 mb-4">
                A colonial-style hotel with traditional Guatemalan decor, multiple courtyards, and a central location. Don Rodrigo offers authentic atmosphere at budget-friendly prices with breakfast included.
              </p>
              <div className="grid md:grid-cols-2 gap-6 mb-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Ideal For:</h4>
                  <p className="text-gray-700">Budget travelers, families, those seeking authentic colonial experience</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Standout Features:</h4>
                  <p className="text-gray-700">Colonial character, central location, breakfast included, authentic Guatemalan hospitality</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-teal-600 mb-2">Pros:</h4>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Authentic colonial atmosphere</li>
                    <li>• Central location</li>
                    <li>• Breakfast included</li>
                    <li>• Good value for money</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-600 mb-2">Considerations:</h4>
                  <ul className="text-gray-700 space-y-1">
                    <li>• No pool</li>
                    <li>• Limited room types</li>
                    <li>• Some rooms lack modern amenities</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Additional budget hotel entries omitted for brevity */}
          </div>
        </div>
      </section>

      {/* Hotels by Travel Style */}
      <section id="by-travel-style" className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12">Best Hotels for Specific Travel Styles</h2>
          
          {/* Content unchanged */}
        </div>
      </section>

      {/* Pricing Guide */}
      <section id="pricing-guide" className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          {/* Content unchanged */}
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Hotel Comparison Table</h2>
          
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-sm">
              <thead className="bg-teal-500 text-white">
                <tr>
                  <th className="px-5 py-4 text-left font-semibold">Hotel</th>
                  <th className="px-5 py-4 text-left font-semibold">Category</th>
                  <th className="px-5 py-4 text-left font-semibold">Area</th>
                  <th className="px-5 py-4 text-left font-semibold">Price Range</th>
                  <th className="px-5 py-4 text-left font-semibold">Best For</th>
                  <th className="px-5 py-4 text-left font-semibold">Walk to Park</th>
                  <th className="px-5 py-4 text-center font-semibold">Pool</th>
                  <th className="px-5 py-4 text-center font-semibold">Rating</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-5 py-4 font-semibold text-gray-900">Casa Santo Domingo</td>
                  <td className="px-5 py-4 text-gray-700">Luxury</td>
                  <td className="px-5 py-4 text-gray-700">East Side</td>
                  <td className="px-5 py-4 text-gray-700">$250-400</td>
                  <td className="px-5 py-4 text-gray-700">Couples, Honeymoon</td>
                  <td className="px-5 py-4 text-gray-700">5 min</td>
                  <td className="px-5 py-4 text-center text-teal-500"><i className="ri-check-line text-xl"></i></td>
                  <td className="px-5 py-4 text-center text-gray-700">9.2/10</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-5 py-4 font-semibold text-gray-900">Porta Hotel Antigua</td>
                  <td className="px-5 py-4 text-gray-700">Luxury</td>
                  <td className="px-5 py-4 text-gray-700">Central Park</td>
                  <td className="px-5 py-4 text-gray-700">$200-350</td>
                  <td className="px-5 py-4 text-gray-700">Couples, Business</td>
                  <td className="px-5 py-4 text-gray-700">1 min</td>
                  <td className="px-5 py-4 text-center text-gray-400"><i className="ri-close-line text-xl"></i></td>
                  <td className="px-5 py-4 text-center text-gray-700">9.0/10</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-5 py-4 font-semibold text-gray-900">Camino Real Antigua</td>
                  <td className="px-5 py-4 text-gray-700">Luxury</td>
                  <td className="px-5 py-4 text-gray-700">South Side</td>
                  <td className="px-5 py-4 text-gray-700">$180-300</td>
                  <td className="px-5 py-4 text-gray-700">Families, Relaxation</td>
                  <td className="px-5 py-4 text-gray-700">10 min</td>
                  <td className="px-5 py-4 text-center text-teal-500"><i className="ri-check-line text-xl"></i></td>
                  <td className="px-5 py-4 text-center text-gray-700">8.8/10</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-5 py-4 font-semibold text-gray-900">Hotel Cirilo</td>
                  <td className="px-5 py-4 text-gray-700">Boutique</td>
                  <td className="px-5 py-4 text-gray-700">Santa Catalina</td>
                  <td className="px-5 py-4 text-gray-700">$120-180</td>
                  <td className="px-5 py-4 text-gray-700">Couples, Solo</td>
                  <td className="px-5 py-4 text-gray-700">3 min</td>
                  <td className="px-5 py-4 text-center text-gray-400"><i className="ri-close-line text-xl"></i></td>
                  <td className="px-5 py-4 text-center text-gray-700">9.3/10</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-5 py-4 font-semibold text-gray-900">Mesón Panza Verde</td>
                  <td className="px-5 py-4 text-gray-700">Boutique</td>
                  <td className="px-5 py-4 text-gray-700">East Side</td>
                  <td className="px-5 py-4 text-gray-700">$140-200</td>
                  <td className="px-5 py-4 text-gray-700">Couples, Foodies</td>
                  <td className="px-5 py-4 text-gray-700">8 min</td>
                  <td className="px-5 py-4 text-center text-gray-400"><i className="ri-close-line text-xl"></i></td>
                  <td className="px-5 py-4 text-center text-gray-700">9.1/10</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-5 py-4 font-semibold text-gray-900">Hotel Convento Santa Catalina</td>
                  <td className="px-5 py-4 text-gray-700">Boutique</td>
                  <td className="px-5 py-4 text-gray-700">Santa Catalina</td>
                  <td className="px-5 py-4 text-gray-700">$100-160</td>
                  <td className="px-5 py-4 text-gray-700">Photographers, Couples</td>
                  <td className="px-5 py-4 text-gray-700">3 min</td>
                  <td className="px-5 py-4 text-center text-gray-400"><i className="ri-close-line text-xl"></i></td>
                  <td className="px-5 py-4 text-center text-gray-700">8.7/10</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-5 py-4 font-semibold text-gray-900">El Convento Boutique Hotel</td>
                  <td className="px-5 py-4 text-gray-700">Boutique</td>
                  <td className="px-5 py-4 text-gray-700">East Side</td>
                  <td className="px-5 py-4 text-gray-700">$110-170</td>
                  <td className="px-5 py-4 text-gray-700">Digital Nomads</td>
                  <td className="px-5 py-4 text-gray-700">5 min</td>
                  <td className="px-5 py-4 text-center text-teal-500"><i className="ri-check-line text-xl"></i></td>
                  <td className="px-5 py-4 text-center text-gray-700">8.9/10</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-5 py-4 font-semibold text-gray-900">Hotel Posada de Don Rodrigo</td>
                  <td className="px-5 py-4 text-gray-700">Budget</td>
                  <td className="px-5 py-4 text-gray-700">Central Park</td>
                  <td className="px-5 py-4 text-gray-700">$60-90</td>
                  <td className="px-5 py-4 text-gray-700">Budget Travelers</td>
                  <td className="px-5 py-4 text-gray-700">4 min</td>
                  <td className="px-5 py-4 text-center text-gray-400"><i className="ri-close-line text-xl"></i></td>
                  <td className="px-5 py-4 text-center text-gray-700">8.3/10</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-5 py-4 font-semibold text-gray-900">Hotel Casa Rustica</td>
                  <td className="px-5 py-4 text-gray-700">Budget</td>
                  <td className="px-5 py-4 text-gray-700">South Side</td>
                  <td className="px-5 py-4 text-gray-700">$50-75</td>
                  <td className="px-5 py-4 text-gray-700">Budget Travelers</td>
                  <td className="px-5 py-4 text-gray-700">6 min</td>
                  <td className="px-5 py-4 text-center text-gray-400"><i className="ri-close-line text-xl"></i></td>
                  <td className="px-5 py-4 text-center text-gray-700">7.8/10</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Booking Tips */}
      <section id="booking-tips" className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          {/* Content unchanged */}
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Frequently Asked Questions</h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">What are the best hotels in Antigua Guatemala?</h3>
              <p className="text-gray-700">
                The best hotels in Antigua Guatemala include <strong>Casa Santo Domingo</strong> (best luxury), <strong>Porta Hotel Antigua</strong> (best location), <strong>Hotel Cirilo</strong> (best boutique), <strong>Mesón Panza Verde</strong> (best romantic), <strong>Camino Real Antigua</strong> (best with pool), and <strong>Posada de Don Rodrigo</strong> (best budget). All are located within the colonial center, offering easy walking access to Central Park, restaurants, and attractions.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">What is the best area to stay in Antigua?</h3>
              <p className="text-gray-700">
                The best area to stay in Antigua Guatemala is within walking distance of Central Park (Parque Central). This puts you in the heart of the colonial center with easy access to restaurants, shops, and attractions. The area around Santa Catalina Arch is particularly popular for its iconic views and central location.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Are hotels in Antigua expensive?</h3>
              <p className="text-gray-700">
                Hotel prices in Antigua vary widely. Budget hotels start around $25-40 per night, mid-range boutique hotels cost $80-150, and luxury properties range from $200-400+ per night. Compared to US hotel prices, Antigua offers excellent value, especially in the boutique and luxury categories.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Is it better to stay inside the colonial center?</h3>
              <p className="text-gray-700">
                Yes, staying inside the colonial center is highly recommended. Antigua is a walking city, and staying centrally means you can easily explore on foot, return to your hotel during the afternoon, and walk safely to dinner. Many of the best hotels in Antigua Guatemala occupy restored colonial buildings with interior courtyards, rooftop terraces offering volcano views, and architectural details that transport you back centuries.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Are Antigua hotels safe?</h3>
              <p className="text-gray-700">
                Yes, hotels in Antigua's colonial center are generally very safe. Most properties have 24-hour security, gated entrances, and secure parking. The tourist police (POLITUR) patrol the central area regularly. Choose hotels with good reviews and security features for peace of mind.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Do hotels include breakfast?</h3>
              <p className="text-gray-700">
                Many hotels in Antigua include breakfast, especially boutique and luxury properties. Budget hotels may charge extra. Breakfast typically includes fresh fruit, eggs, beans, tortillas, and excellent Guatemalan coffee. Always check what's included when booking.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Is Airbnb better than hotels?</h3>
              <p className="text-gray-700">
                Hotels in Antigua often provide better value than Airbnb, especially boutique properties. Hotels offer daily housekeeping, breakfast, concierge services, and better security. Airbnb can work for longer stays or groups, but for typical visits, hotels provide a more complete experience.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Are there hotels with volcano views?</h3>
              <p className="text-gray-700">
                Yes, several hotels in Antigua offer volcano views, particularly of Agua Volcano. Look for properties with rooftop terraces or rooms on upper floors. Hotels on the eastern side of the colonial center tend to have the best volcano views.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">How far is Antigua from the airport?</h3>
              <p className="text-gray-700">
                Antigua is approximately 45 minutes from Guatemala City's La Aurora International Airport (GUA). Most hotels can arrange airport transfers for $30-50 USD. Shared shuttles are also available for around $10-15 per person. <Link to="/guatemala-city-airport-to-antigua" className="text-teal-500 hover:text-teal-600 font-semibold cursor-pointer">Read our complete airport transfer guide</Link>.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">When is hotel season most expensive?</h3>
              <p className="text-gray-700">
                Hotel prices peak during Semana Santa (Holy Week, March or April), Christmas/New Year, and July-August. Semana Santa is the most expensive time, with prices often doubling or tripling. Book well in advance for these periods. The best deals are May-June and September-October.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Is Antigua good for honeymoon?</h3>
              <p className="text-gray-700">
                Yes, Antigua is an excellent honeymoon destination. The colonial architecture, romantic restaurants, luxury boutique hotels, and nearby volcano adventures create a perfect mix of romance and adventure. Many hotels offer honeymoon packages with special amenities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Conclusion Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          {/* Conclusion content unchanged */}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-teal-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Get Personalized Hotel Recommendations</h2>
          <p className="text-xl text-white/90 mb-8">Tell us your travel dates, budget, and preferences, and we'll recommend the perfect hotels for your Antigua trip</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/plan-my-antigua-trip" className="inline-block bg-white hover:bg-gray-100 text-teal-500 px-8 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap cursor-pointer">
              Plan My Trip
            </Link>
            <Link to="/antigua-guatemala-travel-guide" className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap cursor-pointer">
              Read Complete Guide
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
